import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Eye, History, RefreshCw, Send } from 'lucide-react'
import { toast } from 'react-toastify'
import emailNotificationService, { describeError } from '../../services/emailNotificationService'

function formatWhen(value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  })
}

function inr(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
}

function reportError(err, fallback) {
  const { message, handled } = describeError(err, fallback)
  if (!handled) toast.error(message)
}

function triggerLabel(reminder) {
  if (!reminder) return '—'
  if (reminder.triggerType === 'on_due_date') return `On due date at ${reminder.sendTime}`
  if (reminder.triggerType === 'days_before') {
    return `${reminder.daysOffset} day${reminder.daysOffset === 1 ? '' : 's'} before due at ${reminder.sendTime}`
  }
  if (reminder.triggerType === 'days_after') {
    return `${reminder.daysOffset} day${reminder.daysOffset === 1 ? '' : 's'} after due at ${reminder.sendTime}`
  }
  if (reminder.triggerType === 'specific_date') return `On ${reminder.specificDate} at ${reminder.sendTime}`
  return reminder.sendTime || '—'
}

/**
 * Per-event GST Compliance stage panel (schedule + preview/send/history).
 * Recipients stay on the parent Email Notifications form.
 */
export default function GstComplianceEmailPanel({ eventKey, canEdit }) {
  const [stage, setStage] = useState(null)
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [previewing, setPreviewing] = useState(false)
  const [sending, setSending] = useState(false)
  const [preview, setPreview] = useState(null)

  const load = useCallback(async () => {
    if (!eventKey) return
    setLoading(true)
    try {
      const [stageRes, histRes] = await Promise.all([
        emailNotificationService.getGstComplianceStage(eventKey),
        emailNotificationService.gstComplianceHistory(eventKey, { limit: 15 }).catch(() => ({ data: { items: [] } })),
      ])
      setStage(stageRes?.data || null)
      const items = histRes?.data?.items || histRes?.data || []
      setHistory(Array.isArray(items) ? items : [])
    } catch (err) {
      reportError(err, 'Failed to load GST compliance stage')
      setStage(null)
    } finally {
      setLoading(false)
    }
  }, [eventKey])

  useEffect(() => { load() }, [load])

  const handlePreview = async () => {
    setPreviewing(true)
    try {
      const res = await emailNotificationService.previewGstCompliance(eventKey)
      setPreview(res?.data || null)
      toast.success('Preview generated (not emailed)')
    } catch (err) {
      reportError(err, 'Preview failed')
    } finally {
      setPreviewing(false)
    }
  }

  const handleSendNow = async () => {
    if (!canEdit) return
    setSending(true)
    try {
      const res = await emailNotificationService.sendGstComplianceNow(eventKey)
      toast.success(res?.message || 'GST email sent')
      await load()
    } catch (err) {
      reportError(err, 'Send failed')
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return <p className="en-callout">Loading GST compliance stage…</p>
  }

  if (!stage?.reminder) {
    return (
      <p className="en-callout en-callout--warn">
        GST stage not found. Open{' '}
        <Link to="/settings/gst-reminder-settings">GST Reminder &amp; Compliance</Link>
        {' '}to initialise the filing cycle, then refresh.
      </p>
    )
  }

  const { reminder, period, settings } = stage
  const snap = preview?.summary
    ? {
      output: preview.summary.outputTax?.total,
      purchase: preview.summary.purchases?.totalTax,
      expense: preview.summary.expenses?.totalTax,
      payable: preview.summary.estimatedPayable?.total,
      exceptions: preview.summary.exceptions?.total,
    }
    : null

  return (
    <div className="en-mgmt-panel" style={{ marginBottom: 16 }}>
      <div className="en-callout">
        GST cycle: <strong>{settings?.filingFrequency || 'Monthly'}</strong>
        {' · '}due day <strong>{settings?.dueDay ?? 20}</strong>
        {' · '}tz <strong>{settings?.timezone || 'Asia/Kolkata'}</strong>
        {' · '}
        <Link to="/settings/gst-reminder-settings">Edit cycle &amp; calc options</Link>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, margin: '12px 0',
      }}
      >
        <div className="en-callout" style={{ margin: 0 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#64748B' }}>Return period</div>
          <div style={{ fontWeight: 700 }}>{period?.periodLabel || '—'}</div>
        </div>
        <div className="en-callout" style={{ margin: 0 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#64748B' }}>Due date</div>
          <div style={{ fontWeight: 700 }}>{period?.dueDateLocal || '—'}</div>
        </div>
        <div className="en-callout" style={{ margin: 0 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#64748B' }}>Days remaining</div>
          <div style={{ fontWeight: 700 }}>
            {period?.daysRemaining == null ? '—' : period.daysRemaining}
          </div>
        </div>
        <div className="en-callout" style={{ margin: 0 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#64748B' }}>This stage</div>
          <div style={{ fontWeight: 700 }}>{triggerLabel(reminder)}</div>
        </div>
      </div>

      <p style={{ fontSize: 13, color: '#475569', margin: '0 0 12px' }}>
        Each mail includes output GST, purchase GST / ITC, approved expense tax, estimated net payable,
        exceptions and invoice samples — no login required. Stages run from ~15 days before due through
        daily digests for the last 5 days until the cycle closes.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <button type="button" className="en-btn en-btn--ghost" onClick={load} disabled={loading}>
          <RefreshCw size={14} /> Refresh
        </button>
        <button type="button" className="en-btn en-btn--ghost" onClick={handlePreview} disabled={previewing}>
          <Eye size={14} /> {previewing ? 'Previewing…' : 'Preview figures'}
        </button>
        <button
          type="button"
          className="en-btn en-btn--primary"
          onClick={handleSendNow}
          disabled={!canEdit || sending}
        >
          <Send size={14} /> {sending ? 'Sending…' : 'Send now'}
        </button>
      </div>

      {snap ? (
        <div className="en-callout" style={{ marginBottom: 12 }}>
          <strong>Latest preview</strong>
          {' · '}Output {inr(snap.output)}
          {' · '}Purchase GST {inr(snap.purchase)}
          {' · '}Expense tax {inr(snap.expense)}
          {' · '}Net payable {inr(snap.payable)}
          {' · '}Exceptions {snap.exceptions ?? 0}
        </div>
      ) : null}

      <div style={{ marginTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontWeight: 700, fontSize: 13 }}>
          <History size={14} /> Recent sends
        </div>
        {!history.length ? (
          <p style={{ fontSize: 12, color: '#94A3B8' }}>No history for this stage yet.</p>
        ) : (
          <table className="en-table" style={{ width: '100%', fontSize: 12 }}>
            <thead>
              <tr>
                <th align="left">When</th>
                <th align="left">Period</th>
                <th align="left">Status</th>
                <th align="left">Trigger</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h._id}>
                  <td>{formatWhen(h.sentAt || h.createdAt)}</td>
                  <td>{h.gstPeriodLabel || '—'}</td>
                  <td>{h.deliveryStatus || '—'}</td>
                  <td>{h.triggerType || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
